import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { toast } from 'react-toastify';
import categoryService from "../services/categoryService";
import tenantService from "../services/tenantService";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import { colors, getStatusColors, getTableHeaderStyles, getActualTableRowBackground } from "../theme/colors";
import Pagination from "../components/Pagination";
import { isTenantAdmin as isTenantAdminHelper } from "../utils/userHelper";

const Categories = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [categories, setCategories] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Infinite scroll state za mobilne uređaje
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Default 5 za mobilne
  const [formData, setFormData] = useState({
    name: "",
    tenantId: "",
  });


  // Infinite scroll funkcija za mobilne uređaje
  const loadMoreCategories = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      
      const isSuperAdmin = currentUser?.userType === "super_admin";
      const isTenantAdmin = currentUser?.userType === "tenant_admin";
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

      let response;
      if (isSuperAdmin) {
        response = await categoryService.getCategoriesByTenant(selectedTenantId, nextPage, itemsPerPage);
      } else if ((isTenantAdmin || tenantId) && tenantId) {
        response = await categoryService.getCategoriesByTenant(tenantId, nextPage, itemsPerPage);
      } else {
        return;
      }

      const data = response.data || response;
      const newCategories = data.categories || data;
      
      if (Array.isArray(newCategories) && newCategories.length > 0) {
        setCategories(prev => [...prev, ...newCategories]);
        setCurrentPage(nextPage);
        
        // Proveri da li ima još podataka
        const total = parseInt(data.total) || parseInt(data.totalCount) || 0;
        const loadedCount = categories.length + newCategories.length;
        setHasMore(loadedCount < total);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more categories:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [currentUser, selectedTenantId, currentPage, itemsPerPage, loadingMore, hasMore, categories.length]);

  // Scroll event listener za infinite scroll
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        loadMoreCategories();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, loadMoreCategories]);

  // Postavi selectedTenantId u window za Layout komponentu
  useEffect(() => {
    window.selectedTenantId = selectedTenantId;
  }, [selectedTenantId]);

  const fetchTenants = useCallback(async () => {
    try {
      const isSuperAdmin = currentUser?.userType === "super_admin";
      const isTenantAdmin = currentUser?.userType === "tenant_admin";
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

      if (isSuperAdmin) {
        const response = await tenantService.getAllTenants();
        setTenants(response.tenants || response);
      } else if (isTenantAdmin && tenantId) {
        const t = currentUser.tenantId;
        setTenants([
          typeof t === "object" ? t : { _id: t, name: "Your tenant" },
        ]);
      } else {
        setTenants([]);
      }
    } catch (err) {
      console.error("Error fetching tenants:", err);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Reset page when selectedTenantId changes
  useEffect(() => {
    setPage(0);
  }, [selectedTenantId]);

  // Početni učitavanje bez paginacije - bez loading state-a da se izbegne refresh
  useEffect(() => {
    if (selectedTenantId || currentUser?.userType !== "super_admin") {
      // Pozovi fetchCategories bez setovanja loading state-a da se izbegne refresh
      const fetchCategoriesSilently = async () => {
        try {
          const isSuperAdmin = currentUser?.userType === "super_admin";
          const isTenantAdmin = currentUser?.userType === "tenant_admin";
          const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

          let response;
          if (isSuperAdmin) {
            if (!selectedTenantId) {
              setCategories([]);
              setTotalCount(0);
              return;
            }
            response = await categoryService.getCategoriesByTenant(selectedTenantId, page + 1, rowsPerPage);
          } else if ((isTenantAdmin || tenantId) && tenantId) {
            response = await categoryService.getCategoriesByTenant(tenantId, page + 1, rowsPerPage);
          } else {
            setCategories([]);
            setTotalCount(0);
            return;
          }

          // Backend vraća podatke u response.data
          const data = response.data || response;
          const categoriesToSet = data.categories || data;
          setCategories(Array.isArray(categoriesToSet) ? categoriesToSet : []);
          
          // Postavi total count
          const total = parseInt(data.total) || parseInt(data.totalCount) || (data.categories ? data.categories.length : 0);
          setTotalCount(total);
          
          // Sinhronizuj page sa backend-om
          const backendPage = parseInt(data.currentPage) - 1;
          if (backendPage !== page && Math.abs(backendPage - page) > 0) {
            setPage(backendPage);
          }
        } catch (err) {
          console.error("Error fetching categories:", err);
          setCategories([]);
          setTotalCount(0);
        }
      };

      fetchCategoriesSilently();
    }
  }, [selectedTenantId, currentUser?.userType, currentUser?.tenantId, isMobile, itemsPerPage, page, rowsPerPage]);

  // Paginacija - pozovi fetchCategoriesSilently kada se promene page ili rowsPerPage
  useEffect(() => {
    if (page > 0 || rowsPerPage !== 10) { // Pozovi samo ako nije početno stanje
      
      const fetchCategoriesSilently = async () => {
        try {
          const isSuperAdmin = currentUser?.userType === "super_admin";
          const isTenantAdmin = currentUser?.userType === "tenant_admin";
          const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

          let response;
          if (isSuperAdmin) {
            if (!selectedTenantId) {
              setCategories([]);
              setTotalCount(0);
              return;
            }
            response = await categoryService.getCategoriesByTenant(selectedTenantId, page + 1, rowsPerPage);
          } else if ((isTenantAdmin || tenantId) && tenantId) {
            response = await categoryService.getCategoriesByTenant(tenantId, page + 1, rowsPerPage);
          } else {
            setCategories([]);
            setTotalCount(0);
            return;
          }

          // Backend vraća podatke u response.data
          const data = response.data || response;
          const categoriesToSet = data.categories || data;
          setCategories(Array.isArray(categoriesToSet) ? categoriesToSet : []);
          
          // Postavi total count
          const total = parseInt(data.total) || parseInt(data.totalCount) || (data.categories ? data.categories.length : 0);
          setTotalCount(total);
          
          // Sinhronizuj page sa backend-om
          const backendPage = parseInt(data.currentPage) - 1;
          if (backendPage !== page && Math.abs(backendPage - page) > 0) {
            setPage(backendPage);
          }
        } catch (err) {
          console.error("Error fetching categories:", err);
          setCategories([]);
          setTotalCount(0);
        }
      };

      fetchCategoriesSilently();
    }
  }, [page, rowsPerPage, isMobile, itemsPerPage, currentUser?.tenantId, currentUser?.userType, selectedTenantId]);

  const handleOpenDialog = useCallback((category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || "",
        tenantId: category.tenantId?._id || category.tenantId || "",
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: "",
        tenantId: selectedTenantId || "",
      });
    }
    setOpenDialog(true);
  }, [selectedTenantId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Event listener za otvaranje dijaloga iz header dugmeta
  useEffect(() => {
    const handleOpenDialogEvent = () => {
      handleOpenDialog();
    };

    window.addEventListener('openCategoryDialog', handleOpenDialogEvent);
    return () => {
      window.removeEventListener('openCategoryDialog', handleOpenDialogEvent);
    };
  }, [handleOpenDialog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({
      name: "",
      tenantId: selectedTenantId || "",
    });
  };

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const isSuperAdmin = currentUser?.userType === "super_admin";
      const isTenantAdmin = isTenantAdminHelper(currentUser, selectedTenantId);
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

      if (!isSuperAdmin && !isTenantAdmin) {
        toast.error("Nemate dozvolu za kreiranje/uređivanje kategorija");
        return;
      }

      // Validacija obaveznih polja
      if (!formData.name.trim()) {
        toast.error("Naziv kategorije je obavezan");
        return;
      }

      if (isSuperAdmin && !selectedTenantId) {
        toast.error("Morate izabrati klijenta iz dropdown-a");
        return;
      }

      // Build payload
      const dataToSend = {
        ...formData,
        tenantId: !isSuperAdmin && tenantId ? tenantId : selectedTenantId,
      };

      if (!editingCategory) {
        dataToSend.createdBy = currentUser._id;
        dataToSend.createdAt = new Date();
      } else {
        dataToSend.updatedBy = currentUser._id;
      }
      dataToSend.updatedAt = new Date();

      if (editingCategory) {
        const response = await categoryService.updateCategory(editingCategory._id, dataToSend);
        const updatedCategory = response.data || response;
        // Update local state without page refresh
        setCategories(prevCategories => 
          prevCategories.map(cat => 
            cat._id === editingCategory._id ? { ...cat, ...updatedCategory } : cat
          )
        );
      } else {
        const response = await categoryService.createCategory(dataToSend);
        const newCategory = response.data || response;
        
        // Update total count for new item
        setTotalCount(prev => prev + 1);
        
        // If we're on the first page and have space, add to current page
        if (page === 0 && categories.length < rowsPerPage) {
          setCategories(prevCategories => [...prevCategories, newCategory]);
        }
        // If not on first page or page is full, just update count - item will appear when user navigates
      }
      handleCloseDialog();
      toast.success(editingCategory ? "Kategorija je uspešno ažurirana!" : "Kategorija je uspešno kreirana!");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Greška pri čuvanju kategorije");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Da li ste sigurni da želite da obrišete ovu kategoriju?")) {
      try {
        const isSuperAdmin = currentUser?.userType === "super_admin";
        const isTenantAdmin = isTenantAdminHelper(currentUser, selectedTenantId);
        const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

        if (!isSuperAdmin && !isTenantAdmin) {
          toast.error("Nemate dozvolu za brisanje kategorija");
          return;
        }

        if (!isSuperAdmin && tenantId) {
          const categoryToDelete = categories.find((c) => c._id === id);
          if (
            categoryToDelete?.tenantId?._id !== tenantId &&
            categoryToDelete?.tenantId !== tenantId
          ) {
            toast.error("Možete obrisati samo kategorije iz vašeg tenant-a");
            return;
          }
        }

        await categoryService.deleteCategory(id);
        
        // Reload categories silently without page refresh
        const fetchCategoriesSilently = async () => {
          try {
            const isSuperAdmin = currentUser?.userType === "super_admin";
            const isTenantAdmin = currentUser?.userType === "tenant_admin";
            const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

            let response;
            if (isSuperAdmin) {
              if (!selectedTenantId) {
                setCategories([]);
                setTotalCount(0);
                return;
              }
              response = await categoryService.getCategoriesByTenant(selectedTenantId, page + 1, rowsPerPage);
            } else if ((isTenantAdmin || tenantId) && tenantId) {
              response = await categoryService.getCategoriesByTenant(tenantId, page + 1, rowsPerPage);
            } else {
              setCategories([]);
              setTotalCount(0);
              return;
            }

            // Backend vraća podatke u response.data
            const data = response.data || response;
            const categoriesToSet = data.categories || data;
            setCategories(Array.isArray(categoriesToSet) ? categoriesToSet : []);
            
            // Postavi total count
            const total = parseInt(data.total) || parseInt(data.totalCount) || (data.categories ? data.categories.length : 0);
            setTotalCount(total);
          } catch (err) {
            console.error('Error reloading categories:', err);
          }
        };
        
        fetchCategoriesSilently();
        toast.success("Kategorija je uspešno obrisana!");
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || "Greška pri brisanju kategorije");
      }
    }
  };


  return (
    <div>
        {/* Tenant dropdown - samo za super admin */}
        {currentUser?.userType === "super_admin" && (
         <Box sx={{ 
           position: isMobile ? 'sticky' : 'static',
           top: isMobile ? '64px' : 'auto', // AppBar visina
           zIndex: isMobile ? 1000 : 'auto',
           backgroundColor: isMobile ? (isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)') : 'transparent',
           backdropFilter: isMobile ? 'blur(10px)' : 'none',
           p: isMobile ? 2 : 0,
           mb: isMobile ? 2 : 0,
           borderBottom: isMobile ? `1px solid ${customColors.border.light}` : 'none'
         }}>
            <FormControl fullWidth>
              <InputLabel sx={commonStyles.textSecondary}>Klijent</InputLabel>
              <Select
                value={selectedTenantId}
                label="Klijent"
                onChange={(e) => setSelectedTenantId(e.target.value)}
                sx={commonStyles.selectField}
              >
                <MenuItem value="">
                  <em>Izaberite klijenta...</em>
                </MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant._id} value={tenant._id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!selectedTenantId && (
              <Alert severity="info" sx={{ mt: 2, ...commonStyles.alert("info") }}>
                Izaberite klijenta da biste videli njegove kategorije
              </Alert>
            )}
         </Box>
        )}

        {/* Add Category Button - samo za desktop */}
        {!isMobile && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={commonStyles.gradientButton}
              disabled={currentUser?.userType === "super_admin" && !selectedTenantId}
            >
              Dodaj kategoriju
            </Button>
          </Box>
        )}

        {/* Responsive prikaz - tabela za desktop, kartice za mobilne */}
        {isMobile ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2
          }}>
            {Array.isArray(categories) && categories.length > 0 && !(currentUser?.userType === "super_admin" && !selectedTenantId) ? (
              categories.map((category, index) => (
                <Card key={category._id} sx={{
                  background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
                  border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    background: isDarkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 1)',
                    border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)',
                    boxShadow: isDarkMode ? '0 8px 20px rgba(0, 0, 0, 0.4)' : '0 8px 20px rgba(0, 0, 0, 0.15)',
                  },
                  mb: 1.5
                }}>
                  <CardContent sx={{ p: 1.5 }}>
                    {/* Header sa nazivom i akcijama */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      justifyContent: 'space-between',
                      mb: 1.5
                    }}>
                      <Typography variant="h6" sx={{ 
                        ...commonStyles.textPrimary,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        flex: 1
                      }}>
                        {category.name}
                      </Typography>
                      
                      {/* Akcije */}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(category)}
                          sx={{
                            color: customColors.primary.main,
                            '&:hover': {
                              backgroundColor: `${customColors.primary.main}20`,
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Footer sa statusom i klijentom */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pt: 1,
                      borderTop: `1px solid ${customColors.border.light}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ 
                          ...commonStyles.textSecondary,
                          fontSize: '0.7rem'
                        }}>
                          Status:
                        </Typography>
                        <Chip
                          label={category.isActive ? 'Aktivan' : 'Neaktivan'}
                          size="small"
                          color={category.isActive ? 'success' : 'default'}
                          sx={{
                            background: getStatusColors(category.isActive).background,
                            border: getStatusColors(category.isActive).border,
                            color: getStatusColors(category.isActive).light,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      </Box>
                      
                      {/* Klijent (samo za super_admin) */}
                      {currentUser?.userType === "super_admin" && (
                        <Typography variant="caption" sx={{ 
                          ...commonStyles.textSecondary,
                          fontSize: '0.7rem'
                        }}>
                          {(() => {
                            const categoryTenantId = typeof category.tenantId === 'object' ? category.tenantId._id : category.tenantId;
                            const tenant = tenants.find(t => t._id === categoryTenantId);
                            return tenant ? tenant.name : "N/A";
                          })()}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography variant="h6" sx={commonStyles.textSecondary}>
                Nema kategorija
              </Typography>
            )}
            
            {/* Loading indicator za infinite scroll */}
            {loadingMore && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 3 
              }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" sx={commonStyles.textSecondary}>
                  Učitavanje dodatnih kategorija...
                </Typography>
              </Box>
            )}
            
            {/* End of list indicator */}
            {!hasMore && categories.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 2 
              }}>
                <Typography variant="body2" sx={commonStyles.textSecondary}>
                  Prikazano je svih {categories.length} kategorija
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <div>
            {/* Desktop prikaz - tabela */}
            <TableContainer sx={{ backgroundColor: isDarkMode ? 'transparent' : commonStyles.tableContainer.backgroundColor }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: colors.table.header.background,
                  '& .MuiTableCell-head': {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    fontWeight: 600,
                    borderBottom: `1px solid ${customColors.border.light}`
                  }
                }}>
                  <TableCell>Naziv</TableCell>
                  {currentUser?.userType === "super_admin" && (
                    <TableCell>Klijent</TableCell>
                  )}
                  <TableCell>Status</TableCell>
                  <TableCell>Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(categories) && categories.length > 0 && !(currentUser?.userType === "super_admin" && !selectedTenantId) ? (
                  categories.map((category, index) => (
                    <TableRow key={category._id} sx={{
                      backgroundColor: getActualTableRowBackground(index, isDarkMode),
                      '& .MuiTableCell-body': {
                        borderBottom: `1px solid ${customColors.border.light}`,
                        color: commonStyles.textPrimary.color
                      }
                    }}>
                      <TableCell sx={commonStyles.textPrimary}>
                        <Box display="flex" alignItems="center">
                          <CategoryIcon
                            sx={{ mr: 1, color: customColors.primary.main }}
                          />
                          {category.name}
                        </Box>
                      </TableCell>

                      {currentUser?.userType === "super_admin" && (
                        <TableCell sx={commonStyles.textPrimary}>
                          {(() => {
                            const categoryTenantId = typeof category.tenantId === 'object' ? category.tenantId._id : category.tenantId;
                            const tenant = tenants.find(t => t._id === categoryTenantId);
                            return tenant ? tenant.name : "N/A";
                          })()}
                        </TableCell>
                      )}

                      <TableCell>
                        <Chip
                          label="Aktivan"
                          size="small"
                          color="success"
                          sx={{
                            background: getStatusColors(category.isActive).background,
                            border: getStatusColors(category.isActive).border,
                            color: getStatusColors(category.isActive).light,
                          }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(category)}
                          sx={commonStyles.iconButton(customColors.primary.main)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={currentUser?.userType === "super_admin" ? 4 : 3} sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" sx={commonStyles.textSecondary}>
                        {!Array.isArray(categories) 
                          ? "Greška u učitavanju kategorija" 
                          : categories.length === 0 
                            ? "Nema kategorija za prikaz"
                            : "Učitavanje..."
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginacija - samo za desktop */}
          <Pagination
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Redova po stranici:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} od ${count !== -1 ? count : `više od ${to}`}`
            }
          />
          </div>
        )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: commonStyles.dialogPaper,
        }}
      >
        <DialogTitle sx={commonStyles.dialogTitle}>
          {editingCategory ? "Uredi kategoriju" : "Dodaj novu kategoriju"}
        </DialogTitle>
        <DialogContent sx={{ ...commonStyles.dialogContent, pt: 2 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Naziv kategorije"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              sx={commonStyles.formField}
            />

            {/* Prikaži koji tenant je odabran */}
            {currentUser?.userType === "super_admin" && selectedTenantId && (
              <Box sx={{ mt: 3, p: 2, ...commonStyles.glassContainer, background: colors.status.error.background }}>
                <Typography variant="body2" sx={commonStyles.textSecondary}>
                  Klijent: <strong>{tenants.find(t => t._id === selectedTenantId)?.name}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{
          ...commonStyles.dialogActions,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Delete dugme - samo za editovanje - na levoj strani */}
          <Box>
            {editingCategory && (
              <Button
                onClick={() => handleDelete(editingCategory._id)}
                variant="outlined"
                startIcon={<DeleteIcon />}
                sx={{
                  color: colors.actions.delete,
                  borderColor: colors.actions.delete,
                  '&:hover': {
                    backgroundColor: `${colors.actions.delete}20`,
                    borderColor: colors.actions.delete,
                  }
                }}
              >
                Obriši
              </Button>
            )}
          </Box>
          
          {/* Ostala dugmad na desnoj strani */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCloseDialog} variant="outlined" sx={commonStyles.cancelButton}>
              Otkaži
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={commonStyles.gradientButton}
              disabled={submitting}
            >
              {editingCategory ? "Ažuriraj" : "Kreiraj"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Categories;
